/**
 * The builder / first-GF wizard modal body — collects her `Persona` + `Appearance`
 * (the SAME contracts the server-side wizard validates) and runs the two builder
 * paths over the verified endpoints: describe (create → build) or upload (create →
 * ingest a moderated concept image). Character consistency (pinned seed +
 * reference) is entirely the server's job; this only gathers and validates input
 * against the `@4f/shared` schemas, so a minor persona is structurally
 * unrepresentable (adult-only `AgeRange`, compliance red line 1).
 */
import { AgeRange, AppearanceStyle, type BuildSource, isOk, NsfwLevel } from '@4f/shared';
import { Button, Form, Radio, Select, type SelectOption, Textarea, TextInput, useT } from '@4f/ui';
import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import type { CompanionView } from '../../api/types.ts';
import type { BuilderStore } from '../../stores/builder.ts';
import type { PrebuiltStore } from '../../stores/prebuilt.ts';
import { BuilderPicker } from './BuilderPicker.tsx';
import { type BuilderForm, emptyBuilderForm, invalidBuilderFields } from './builder-model.ts';
import { type BuilderChoice, builderDestination } from './builder-picker.ts';
import { PrebuiltCatalog } from './PrebuiltCatalog.tsx';

/** Prettify a raw enum value for a select option label (`2d` → `2D`, else capitalize). */
function optionLabel(value: string): string {
  if (value === '2d' || value === '3d') {
    return value.toUpperCase();
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const toOptions = (values: readonly string[]): SelectOption[] =>
  values.map((value) => ({ value, label: optionLabel(value) }));

/** The persona/appearance text fields rendered as `TextInput`s (selects handled apart). */
const TEXT_FIELDS: readonly { readonly key: keyof BuilderForm; readonly labelKey: string }[] = [
  { key: 'name', labelKey: 'builder.fields.name' },
  { key: 'traits', labelKey: 'builder.fields.traits' },
  { key: 'body', labelKey: 'builder.fields.body' },
  { key: 'hair', labelKey: 'builder.fields.hair' },
  { key: 'eyes', labelKey: 'builder.fields.eyes' },
  { key: 'ethnicity', labelKey: 'builder.fields.ethnicity' },
  { key: 'outfit', labelKey: 'builder.fields.outfit' },
];

export interface BuilderSurfaceProps {
  readonly store: BuilderStore;
  /** The free catalog behind the prebuilt door (clone a template, no charge). */
  readonly prebuilt: PrebuiltStore;
  /** Called with the finished companion after a successful create + build. */
  onCreated(companion: CompanionView): void;
  /**
   * Called with the picked funnel door before setup begins. The parent routes it
   * ({@link builderDestination}): `custom` → billing FIRST (paid upfront), so the
   * builder yields to the billing surface; `prebuilt` → the free CATALOG, which
   * stays here. Omitted ⇒ the picker is skipped (straight to the custom form).
   */
  onChoose?(choice: BuilderChoice): void;
}

export function BuilderSurface(props: BuilderSurfaceProps): JSX.Element {
  const { t } = useT();
  const [form, setForm] = createStore<BuilderForm>(emptyBuilderForm());
  const [mode, setMode] = createSignal<BuildSource>('describe');
  const [file, setFile] = createSignal<File | null>(null);
  const [showErrors, setShowErrors] = createSignal(false);
  // The picker is step 1 only when the parent wires `onChoose`; otherwise the
  // surface opens straight on the setup form (its prior behaviour).
  const [choice, setChoice] = createSignal<BuilderChoice | null>(props.onChoose ? null : 'custom');

  const handleChoose = (picked: BuilderChoice): void => {
    props.onChoose?.(picked);
    // Custom is paid upfront → the parent opens billing and this surface is
    // replaced, so stay on the picker. Prebuilt is the free start → advance to
    // the ready-made catalog here (NEVER the paid describe/upload form).
    if (builderDestination(picked) === 'catalog') {
      setChoice(picked);
    }
  };

  /** Where the picked door leads, or `null` while the picker is still showing. */
  const destination = (): 'catalog' | 'billing' | null => {
    const picked = choice();
    return picked ? builderDestination(picked) : null;
  };

  const invalid = createMemo(() => invalidBuilderFields(form));
  const missingImage = createMemo(() => mode() === 'upload' && !file());
  const fieldInvalid = (key: keyof BuilderForm): boolean => showErrors() && invalid().includes(key);

  const pickFile = (event: Event): void => {
    const input = event.currentTarget as HTMLInputElement;
    setFile(input.files?.[0] ?? null);
  };

  const submit = async (): Promise<void> => {
    if (invalid().length > 0 || missingImage()) {
      setShowErrors(true);
      return;
    }
    const result = await props.store.submit({
      mode: mode(),
      form: { ...form },
      file: file() ?? undefined,
    });
    if (result && isOk(result)) {
      props.onCreated(result.value);
    }
  };

  const modeOptions = [
    { value: 'describe', label: t('builder.mode.describe') },
    { value: 'upload', label: t('builder.mode.upload') },
  ];

  return (
    <Show when={choice()} fallback={<BuilderPicker onChoose={handleChoose} />}>
      <Show
        when={destination() === 'catalog'}
        fallback={
          <Form onSubmit={() => void submit()} class="f4-builder" aria-label={t('builder.title')}>
            <p class="f4-genui__prompt">{t('builder.intro')}</p>

            <Radio
              name="f4-builder-mode"
              legend={t('builder.mode.legend')}
              options={modeOptions}
              value={mode()}
              onChange={(value) => setMode(value as BuildSource)}
            />

            <Select
              id="f4-builder-nsfw"
              label={t('builder.fields.nsfwLevel')}
              options={toOptions(NsfwLevel.options)}
              value={form.nsfwLevel}
              onValue={(value) => setForm('nsfwLevel', value)}
            />
            <Select
              id="f4-builder-style"
              label={t('builder.fields.style')}
              options={toOptions(AppearanceStyle.options)}
              value={form.style}
              onValue={(value) => setForm('style', value)}
            />
            <Select
              id="f4-builder-age"
              label={t('builder.fields.ageRange')}
              options={toOptions(AgeRange.options)}
              value={form.ageRange}
              onValue={(value) => setForm('ageRange', value)}
            />

            {/* Her backstory is prose, not a label — a single-line input made
                anything past a few words invisible while typing. */}
            <Textarea
              id="f4-builder-backstory"
              label={t('builder.fields.backstory')}
              value={form.backstory}
              invalid={fieldInvalid('backstory')}
              rows={4}
              onValue={(value) => setForm('backstory', value)}
            />

            <For each={TEXT_FIELDS}>
              {(field) => (
                <TextInput
                  id={`f4-builder-${field.key}`}
                  label={t(field.labelKey)}
                  value={form[field.key]}
                  invalid={fieldInvalid(field.key)}
                  onValue={(value) => setForm(field.key, value)}
                />
              )}
            </For>

            <Show when={mode() === 'upload'}>
              <label class="f4-builder__file">
                <span>{t('builder.upload.pick')}</span>
                <input
                  type="file"
                  class="f4-genui-file__input"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={pickFile}
                />
                <span class="f4-genui__hint">{t('builder.upload.hint')}</span>
              </label>
            </Show>

            <Show when={showErrors() && (invalid().length > 0 || missingImage())}>
              <p class="f4-genui__error" role="alert">
                {missingImage() ? t('builder.imageRequired') : t('builder.incomplete')}
              </p>
            </Show>
            <Show when={props.store.error()}>
              {(error) => (
                <p class="f4-genui__error" role="alert">
                  {error().message}
                </p>
              )}
            </Show>

            <Button type="submit" variant="primary" disabled={props.store.submitting()}>
              {props.store.submitting() ? t('builder.submitting') : t('builder.submit')}
            </Button>
          </Form>
        }
      >
        <PrebuiltCatalog store={props.prebuilt} onCreated={props.onCreated} />
      </Show>
    </Show>
  );
}
